/**
 * This file is part of the Haiman.
 * @link     : https://haiman.io/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 Hangzhou Haila Network Technology Co., Ltd.
 */

import { CHAT, USER, COMMON } from '@/store/types';
import * as api from '@/api/chat';
import store from '@/store/index';
import { StoreActionEnv } from '@/store/actions';
import { TIMConversation } from '@/api/types/chat';
import { chatLogin } from '@/utils/authorization';

const UID_SORT = {
  system: -2,
  support: -1,
};

const sorterUid = (p: TIMConversation, q: TIMConversation): number => {
  const a = UID_SORT[p.conversationID] || 0;
  const b = UID_SORT[q.conversationID] || 0;
  if (a === b) {
    return 0;
  }
  return a > b ? 1 : -1;
};

export interface StoreChatConversationListState {
  loaded: boolean;
  list: TIMConversation[];
  maxCount: number;
}

const updateBadge = (list: TIMConversation[]): void => {
  // update badge
  let unread = 0;
  list.forEach((item) => {
    // if (item.conversationID === 'C2Csystem') {
    // item.unreadCount = 0;
    // }
    unread += item.unreadCount;
  });
  store.commit(`common/badge/${COMMON.GET_BADGES}`, {
    status: 'update',
    type: 'message',
    data: {
      assign: {
        chat: unread || void 0,
      },
    },
  });
};

export default {
  namespaced: true,
  state: {
    loaded: false,
    list: [],
    maxCount: 50,
  },
  actions: {
    [CHAT.GET_CONVERSATION_LIST](
      { commit, state }: StoreActionEnv<StoreChatConversationListState>,
      { reload, silent } = { reload: false, silent: false },
    ) {
      if (!state.loaded) {
        reload = true;
      }
      if (reload) {
        return new Promise((resolve, reject) => {
          chatLogin().then(({ appid }) => {
            api.getConversationList(appid).then((res) => {
              const list = res.data.sort(sorterUid);
              store.dispatch(`user/im/${USER.GET_IM_USER_INFO}`, {
                silent,
                uids: list.map(p => p.userProfile.userID),
              }).then(() => {
                commit(CHAT.GET_CONVERSATION_LIST, { list, full: true });
                resolve();
              }).catch(reject);
            }).catch(reject);
          }).catch(reject);
        });
      }
      return Promise.resolve();
    },
    [CHAT.DELETE_CONVERSATION]({ commit }, { cid }: { cid: string }) {
      return new Promise((resolve, reject) => {
        chatLogin().then(({ appid }) => {
          api.deleteConversation(appid, cid).then(() => {
            commit(CHAT.DELETE_CONVERSATION, {
              cid,
            });
            resolve();
          }).catch(reject);
        }).catch(reject);
      });
    },
    [CHAT.SET_MESSAGE_READ]({ commit }, { cid }: { cid: string }) {
      return new Promise((resolve, reject) => {
        chatLogin().then(({ appid }) => {
          api.setMessageRead(appid, cid);
          commit(CHAT.SET_MESSAGE_READ, { cid });
          resolve();
        }).catch(reject);
      });
    },
  },
  mutations: {
    [CHAT.GET_CONVERSATION_LIST](state: StoreChatConversationListState, { list, full }: { list: TIMConversation[]; full: boolean }) {
      const newList: TIMConversation[] = list.map(_ => _);
      state.list.forEach((localSess) => {
        const i = newList.findIndex(p => p.conversationID === localSess.conversationID);
        if (i >= 0) {
          newList[i] = Object.assign({}, localSess, newList[i]);
        } else {
          newList.push(localSess);
        }
      });
      state.list = newList.sort(sorterUid);
      if (full) {
        state.loaded = true;
      }
      updateBadge(state.list);
    },
    [CHAT.DELETE_CONVERSATION](state: StoreChatConversationListState, { cid }: { cid: string }) {
      state.list = state.list.filter(p => p.conversationID !== cid);
    },
    [CHAT.SET_MESSAGE_READ](state: StoreChatConversationListState, { cid }: { cid: string }) {
      const c = state.list.find(p => p.conversationID === cid);
      if (c) {
        c.unreadCount = 0;
      }
      updateBadge(state.list);
    },
  },
};
